// RowActions — the action cell at the end of every table row.
//
// Owner ruling 2026-08-25: **primary inline, the rest in a ⋮ menu**. The one
// action an operator takes constantly (usually Edit) is a single click; the
// long tail sits behind the kebab so dense tables stay readable and a
// destructive item is never adjacent to the thing you actually wanted.
//
// Destructive items are rendered in red, pushed below a divider, and the
// CALLER is expected to route them through <ConfirmDialog> — this component
// deliberately does not confirm on its own, because only the caller knows what
// the record is called.

import { cn } from '@fajrarabi/ui';
import { useEffect, useId, useRef, useState } from 'react';

import { Icon, type IconName } from './Icon';

export interface RowAction {
  /** Stable key; also used as the React key. */
  id: string;
  label: string;
  icon?: IconName;
  onSelect: () => void;
  /** Red styling + placed below the divider. Confirm in the caller. */
  destructive?: boolean;
  disabled?: boolean;
  /** Why it is disabled — shown as a title so the reason is discoverable. */
  disabledReason?: string;
}

export interface RowActionsProps {
  /** Rendered inline as a single icon button. The one-click action. */
  primary?: RowAction;
  /** Everything else, behind the ⋮. */
  actions?: readonly RowAction[];
  /** Names the row for screen readers: "Actions for About us". */
  label: string;
  className?: string;
}

export function RowActions({ primary, actions = [], label, className }: RowActionsProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const menuId = useId();

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const menuActions = actions.filter(Boolean);
  const ordinary = menuActions.filter((a) => !a.destructive);
  const destructive = menuActions.filter((a) => a.destructive);

  return (
    <div
      ref={wrapRef}
      className={cn('relative flex items-center justify-end gap-0.5', className)}
      // Rows are usually clickable; an action must never also trigger the row.
      onClick={(e) => e.stopPropagation()}
    >
      {primary && (
        <button
          type="button"
          onClick={primary.onSelect}
          disabled={primary.disabled}
          aria-label={`${primary.label} — ${label}`}
          title={primary.disabled ? primary.disabledReason : primary.label}
          className="grid h-8 w-8 place-items-center rounded-lg text-admin-400 transition-colors hover:bg-admin-100 hover:text-admin-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <Icon name={primary.icon ?? 'edit'} className="h-4 w-4" />
        </button>
      )}

      {menuActions.length > 0 && (
        <>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-label={`Actions for ${label}`}
            aria-expanded={open}
            aria-haspopup="menu"
            aria-controls={menuId}
            className={cn(
              'grid h-8 w-8 place-items-center rounded-lg transition-colors',
              open
                ? 'bg-admin-100 text-admin-700'
                : 'text-admin-400 hover:bg-admin-100 hover:text-admin-700',
            )}
          >
            <Icon name="more" className="h-4 w-4" />
          </button>

          {open && (
            <div
              id={menuId}
              role="menu"
              className="absolute end-0 top-full z-30 mt-1 w-52 rounded-xl border border-admin-200 bg-white p-1.5 shadow-lg"
            >
              {ordinary.map((a) => (
                <MenuItem key={a.id} action={a} onDone={() => setOpen(false)} />
              ))}
              {destructive.length > 0 && ordinary.length > 0 && (
                <div className="my-1.5 border-t border-admin-100" />
              )}
              {destructive.map((a) => (
                <MenuItem key={a.id} action={a} onDone={() => setOpen(false)} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MenuItem({ action, onDone }: { action: RowAction; onDone: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={action.disabled}
      title={action.disabled ? action.disabledReason : undefined}
      onClick={() => {
        if (action.disabled) return;
        onDone();
        // Defer so the menu unmounts before the action opens a dialog —
        // otherwise focus lands on a node that is about to be removed.
        setTimeout(() => action.onSelect(), 0);
      }}
      className={cn(
        'flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40',
        action.destructive
          ? 'text-error-700 hover:bg-error-50'
          : 'text-admin-700 hover:bg-admin-50',
      )}
    >
      {action.icon && (
        <Icon
          name={action.icon}
          className={cn('h-4 w-4', action.destructive ? 'text-error-500' : 'text-admin-400')}
        />
      )}
      {action.label}
    </button>
  );
}

/**
 * The standard actions column. Spread into a `columns` array so every table
 * places actions identically — last, right-aligned, never hidden.
 */
export function actionsColumn<T>(config: {
  label: (row: T) => string;
  primary?: (row: T) => RowAction | undefined;
  actions?: (row: T) => RowAction[];
}) {
  return {
    key: 'actions',
    header: '',
    align: 'end' as const,
    alwaysVisible: true,
    width: '5.5rem',
    render: (row: T) => (
      <RowActions
        label={config.label(row)}
        primary={config.primary?.(row)}
        actions={config.actions?.(row) ?? []}
      />
    ),
  };
}
